import { Download, Upload } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { type ImportGroupKey, importGroup } from "@/shared/import/import-groups";

export interface ImportSummary {
  created: number;
  updated: number;
  errors: number;
}

/// File picker for an import. The sample download is passed in rather than
/// read from a group, because the sample lives with the feature that owns the
/// columns and `shared/` never imports from `features/`.
export function CsvUploadStep({
  groupKey,
  onFile,
  onBack,
  onDownloadSample,
}: {
  groupKey: ImportGroupKey;
  onFile: (file: File) => void;
  onBack: () => void;
  onDownloadSample?: () => void;
}) {
  const [filename, setFilename] = useState<string | null>(null);
  const group = importGroup(groupKey);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <Label htmlFor="csv-file" className="cursor-pointer">
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-border bg-surface-subtle px-6 py-10 text-center transition-colors hover:border-violet-300 hover:bg-pulse-soft/30">
          <Upload className="size-6 text-text-tertiary" aria-hidden="true" />
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">{filename ?? "Click to choose a CSV file"}</span>
            <span className="text-xs text-text-secondary">
              {filename ? "Click again to pick a different file." : "Or drag one in here."}
            </span>
          </div>
        </div>
        <Input
          id="csv-file"
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setFilename(file.name);
            onFile(file);
          }}
        />
      </Label>

      {onDownloadSample ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-subtle px-3 py-2 text-xs">
          <span className="text-text-secondary">Not sure what format?</span>
          <button
            type="button"
            onClick={onDownloadSample}
            className="inline-flex items-center gap-1 font-medium text-text-brand-strong underline-offset-2 hover:underline"
          >
            <Download className="size-3" aria-hidden="true" />
            Download sample CSV
          </button>
        </div>
      ) : null}

      <p className="text-xs text-text-tertiary">{group.uploadHint}</p>
      <div className="mt-auto">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
      </div>
    </div>
  );
}

/// Result screen. `note` is the caller's, because what an import means once it
/// lands (invitations sent, rows queued for the next run) belongs to the
/// feature rather than to the mapping machinery.
export function ImportDoneStep({
  summary,
  onAddMore,
  onClose,
  note,
}: {
  summary: ImportSummary;
  onAddMore: () => void;
  onClose: () => void;
  note?: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div
        aria-live="polite"
        className="flex flex-col gap-3 rounded-lg border border-border bg-pulse-soft/40 p-4"
      >
        <p className="text-sm font-semibold text-text-primary">Import complete</p>
        <dl className="grid grid-cols-3 gap-3">
          <Stat label="Inserted" value={summary.created} />
          <Stat label="Updated" value={summary.updated} />
          <Stat label="Skipped" value={summary.errors} />
        </dl>
        {note}
      </div>
      <div className="mt-auto flex items-center justify-between gap-2">
        <Button type="button" variant="outline" onClick={onAddMore}>
          Add more
        </Button>
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-2xs uppercase tracking-wider text-text-tertiary">{label}</dt>
      <dd className="text-xl font-semibold tabular-nums text-text-primary">{value}</dd>
    </div>
  );
}
